import * as React from "react"
import { LucideIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { GlassCard } from "./glass-card"

export interface StatCardProps {
  title: string
  value: string | number
  icon?: LucideIcon
  trend?: {
    value: number
    isPositive: boolean
  }
  description?: string
  variant?: "default" | "success" | "warning" | "danger" | "info"
  loading?: boolean
  className?: string
}

export type StatCardVariant = NonNullable<StatCardProps["variant"]>

const variantColors: Record<StatCardVariant, string> = {
  default: "from-primary to-primary/80",
  success: "from-success to-success/80",
  warning: "from-warning to-warning/80",
  danger: "from-destructive to-destructive/80",
  info: "from-secondary to-secondary/80",
}

const variantGlows: Record<StatCardVariant, string> = {
  default: "shadow-primary/30",
  success: "shadow-success/30",
  warning: "shadow-warning/30",
  danger: "shadow-destructive/30",
  info: "shadow-secondary/30",
}

/**
 * Classi del wrapper card della StatCard, fonte unica condivisa tra il tenant
 * (ui-frontend) e il master (ui-master). Il bordo NON è qui: deriva dalla
 * GlassCard scelta dal package consumer, così ciascuna superficie eredita lo
 * stile bordo dalla propria singola sorgente.
 */
export function statCardWrapperClassName(
  variant: StatCardVariant = "default",
  className?: string,
): string {
  return cn(
    "group hover:scale-[1.02] transition-all duration-300",
    "hover:shadow-2xl",
    variantGlows[variant],
    className,
  )
}

export type StatCardBodyProps = Omit<StatCardProps, "className">

/**
 * Contenuto interno (presentazionale) della StatCard, senza la GlassCard di
 * wrapping. Condiviso 1:1 tra tenant e master per evitare duplicazione: il
 * solo punto di divergenza è quale GlassCard avvolge questo body.
 */
export function StatCardBody({
  title,
  value,
  icon: Icon,
  trend,
  description,
  variant = "default",
  loading = false,
}: StatCardBodyProps) {
  return (
    <div className="p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-muted-foreground/80 mb-1">
            {title}
          </p>
          {loading ? (
            <div className="h-8 w-32 bg-white/10 rounded animate-pulse" />
          ) : (
            <p className="text-3xl font-bold text-foreground">
              {value}
            </p>
          )}
          {description && (
            <p className="text-xs text-muted-foreground/60 mt-1">
              {description}
            </p>
          )}
          {trend && (
            <div
              className={cn(
                "inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-medium",
                trend.isPositive
                  ? "bg-success/10 text-success"
                  : "bg-destructive/10 text-destructive"
              )}
            >
              <span>{trend.isPositive ? "↑" : "↓"}</span>
              <span>{Math.abs(trend.value)}%</span>
            </div>
          )}
        </div>
        {Icon && (
          <div
            className={cn(
              "p-3 rounded-lg bg-gradient-to-br transition-all duration-300",
              variantColors[variant],
              "group-hover:scale-110 group-hover:rotate-3"
            )}
          >
            <Icon className="h-6 w-6 text-white" />
          </div>
        )}
      </div>
    </div>
  )
}

export function StatCard({
  variant = "default",
  className,
  ...body
}: StatCardProps) {
  return (
    <GlassCard
      variant="elevated"
      className={statCardWrapperClassName(variant, className)}
    >
      <StatCardBody variant={variant} {...body} />
    </GlassCard>
  )
}

export default StatCard
